import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /terms - Whistll User Terms and Conditions (last updated April 2026).
 * Copy transcribed from ~/Documents/Whistll/whistll-all-documents/
 * whistll-user-terms.pdf. Keep in sync when legal updates anything.
 *
 * Venue providers are subject to the separate Whistll Venue Partner
 * Agreement (stored in the same documents folder).
 */
export function TermsPage() {
  return (
    <LegalPlaceholder title="Terms of Service" lastUpdated="April 2026">
      <h2>1. Introduction</h2>
      <p>
        Welcome to Whistll. These Terms and Conditions ("Terms") govern
        your use of the Whistll mobile application and any related
        websites, content, functionality, and services made available
        through it (together, the "App"), operated by Whistll ("we", "us",
        or "our").
      </p>
      <p>
        By downloading, registering for, accessing, or using Whistll, you
        agree to be bound by these Terms. If you do not agree, you must
        not use the App.
      </p>
      <p>
        Whistll is a basketball-focused social and booking platform that
        enables users to discover games, connect with other players,
        organise sessions, share basketball-related content, and make
        bookings with third-party venue providers. Whistll is a technology
        platform only. Unless expressly stated otherwise, Whistll does not
        own, lease, manage, operate, supervise, or control any courts,
        venues, facilities, events, or sessions listed on or accessed
        through the App.
      </p>
      <p>
        Whistll operates in multiple jurisdictions, including the United
        Kingdom, United States, Canada, Australia, and New Zealand. Where
        relevant, jurisdiction-specific provisions apply and are set out
        in these Terms.
      </p>
      <p>
        These Terms apply to players and other individual users of the
        App. Venue providers are subject to the separate Whistll Venue
        Partner Agreement.
      </p>

      <h2>2. Eligibility &amp; user accounts</h2>
      <h3>2.1 Age requirement</h3>
      <p>
        You must be at least 18 years old to create an account or use
        Whistll. By creating an account or otherwise using the App, you
        confirm and warrant that you are 18 years of age or older. We
        reserve the right to request proof of age at any time and to
        suspend or terminate access if we reasonably believe the age
        requirement is not met.
      </p>
      <h3>2.2 Account registration</h3>
      <p>
        To access certain features you may be required to register for an
        account. You agree to:
      </p>
      <ul>
        <li>provide true, accurate, current, and complete information;</li>
        <li>keep your account information updated;</li>
        <li>keep your login credentials secure and confidential;</li>
        <li>
          notify us immediately if you suspect any unauthorised access to
          or use of your account; and
        </li>
        <li>accept responsibility for all activity that occurs under your account.</li>
      </ul>
      <h3>2.3 One account per user</h3>
      <p>
        Each user may hold only one active personal account unless we
        expressly authorise otherwise in writing. The creation or
        operation of multiple, misleading, or duplicate accounts may
        result in suspension or termination of some or all associated
        accounts.
      </p>
      <h3>2.4 Account termination or suspension</h3>
      <p>
        We reserve the right, at our sole discretion, to suspend, restrict,
        or permanently terminate your account at any time, including where:
      </p>
      <ul>
        <li>you breach these Terms;</li>
        <li>we suspect fraud, abuse, misconduct, or misuse of the App;</li>
        <li>
          your conduct exposes Whistll, other users, or third parties to
          legal, reputational, or commercial risk; or
        </li>
        <li>your account has been inactive for a prolonged period.</li>
      </ul>

      <h2>3. Use of the app</h2>
      <h3>3.1 Permitted use</h3>
      <p>Subject to these Terms, you may use the App to:</p>
      <ul>
        <li>discover and join basketball games or sessions;</li>
        <li>organise basketball-related activities as a host;</li>
        <li>connect and communicate with other users through messaging, follows, and social features;</li>
        <li>make bookings with third-party venue providers;</li>
        <li>upload, post, or share basketball-related content, including highlights and clips; and</li>
        <li>participate in competitive features including team challenges, leagues, and rankings.</li>
      </ul>
      <h3>3.2 Prohibited conduct</h3>
      <p>You agree that you will not:</p>
      <ul>
        <li>use the App for any unlawful, harmful, fraudulent, or improper purpose;</li>
        <li>harass, threaten, abuse, bully, intimidate, stalk, or harm any other user or third party;</li>
        <li>post false, misleading, deceptive, defamatory, or fraudulent content;</li>
        <li>impersonate any person, organisation, or entity, or misrepresent your identity or affiliation;</li>
        <li>interfere with, damage, disrupt, or compromise the operation, security, or integrity of the App;</li>
        <li>attempt to gain unauthorised access to any part of the App, related systems, accounts, or networks;</li>
        <li>use bots, scrapers, crawlers, scripts, or other automated means without our prior written consent;</li>
        <li>upload or transmit malware, viruses, malicious code, or harmful material;</li>
        <li>use the App in a way that infringes any intellectual property, privacy, publicity, contractual, or other rights of any person;</li>
        <li>create fake games, post misleading challenges, or use the platform to harass other users;</li>
        <li>share venue access codes, booking details, or other users' personal information outside the platform; or</li>
        <li>engage in any conduct that, in our sole judgment, is harmful to users, venue partners, third parties, or the reputation or operation of Whistll.</li>
      </ul>
      <h3>3.3 Third-party collaborations and integrations</h3>
      <p>
        Whistll may collaborate with, integrate with, or facilitate
        bookings through third-party venue providers and booking
        platforms. Your use of any third-party services is subject to
        those third parties' own terms and policies. Whistll is not
        responsible for the acts, omissions, content, availability, or
        terms of any third party, except to the extent required by
        applicable law.
      </p>

      <h2>4. Bookings &amp; payments</h2>
      <h3>4.1 Role of Whistll</h3>
      <p>
        Whistll operates solely as an intermediary technology platform
        that facilitates bookings between players and third-party venue
        providers. Whistll does not own, operate, control, supervise, or
        manage any venue, court, or facility made available through the
        App and is not the supplier of the booked service.
      </p>
      <h3>4.2 Pre-authorisation payment flow</h3>
      <p>
        When you join a paid game, your payment method is pre-authorised
        for your share of the court fee plus the Whistll service fee. You
        will not be charged unless and until the game is confirmed.
      </p>
      <p>
        A game is confirmed only when the required number of players for
        the game format has been reached. If the required number is not
        reached by one (1) hour before the scheduled tip-off time, the
        game is automatically cancelled and any pre-authorisation is
        released in full. You will not be charged.
      </p>
      <p>
        A pending selection, joined slot, or pre-authorisation does not
        constitute a confirmed booking.
      </p>
      <h3>4.3 Game creation and cancellation windows</h3>
      <ul>
        <li>
          <strong>New games</strong> cannot be created for a time slot
          within two (2) hours of the scheduled tip-off.
        </li>
        <li>
          <strong>Automatic cancellation</strong> - if the required player
          count is not met by one (1) hour before tip-off, the game is
          cancelled and all pre-authorisations are released.
        </li>
        <li>
          <strong>Host cancellation</strong> - the host cannot manually
          cancel a confirmed game within twenty-four (24) hours of the
          scheduled tip-off.
        </li>
        <li>
          <strong>Player cancellation</strong> - you cannot cancel your
          participation in a confirmed game within twenty-four (24) hours
          of the scheduled tip-off.
        </li>
        <li>
          <strong>Host removal of players</strong> - a host may remove a
          player from a game at any time before tip-off. If the removed
          player has already paid, the host's payment method is charged
          the removed player's share in full. The host will be clearly
          notified of this charge before removal is processed.
        </li>
      </ul>
      <h3>4.4 Platform fees</h3>
      <p>
        Whistll charges a platform fee of twenty percent (20%) on top of
        the venue's court fee per player. This fee is in addition to, and
        does not reduce, the amount payable to the venue.
      </p>
      <p>
        <strong>Example:</strong> Where the venue's court fee is £12 per
        player, you will pay £14.40 total per player (£12 to the venue
        and £2.40 to Whistll). The exact fee will always be shown before
        you confirm payment.
      </p>
      <h3>4.5 Refunds</h3>
      <p>
        Where a game is automatically cancelled under clause 4.2 or
        4.3(b), pre-authorisations are released in full and no charges
        are made.
      </p>
      <p>
        Where a game is confirmed and subsequently cancelled by the host
        before the 24-hour cancellation cut-off, players will receive a
        full refund of both the court fee and the Whistll platform fee.
      </p>
      <p>
        Where a player cancels their participation before the 24-hour
        cut-off, they will receive a full refund of both the court fee
        and the Whistll platform fee.
      </p>
      <p>
        Outside of these circumstances, refunds are at Whistll's
        discretion (see clause 4.7).
      </p>
      <h3>4.6 Venue terms apply</h3>
      <p>
        All bookings are subject to the rules and policies of the
        relevant venue provider, including access requirements, conduct
        rules, weather or operational disruptions, and venue-specific
        safety requirements. By making a booking, you agree to be bound
        by applicable venue terms.
      </p>
      <h3>4.7 Extenuating circumstances</h3>
      <p>
        You may submit a claim to Whistll at{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>{" "}
        requesting a refund or alternative remedy where genuine
        unforeseen circumstances affect your ability to use a booking.
        Any such claim must:
      </p>
      <ul>
        <li>
          be submitted as soon as reasonably practicable and no later
          than fourteen (14) days after the booking date;
        </li>
        <li>include a clear description of the circumstances; and</li>
        <li>include any supporting evidence where reasonably available.</li>
      </ul>
      <p>
        Whistll will review all claims at its sole discretion. Where we
        determine extenuating circumstances apply, we may offer a full
        or partial refund, Whistll App credits, or such other remedy as
        we consider fair. Any such decision does not constitute an
        admission of liability or a precedent for future claims.
      </p>
      <p>
        Examples of circumstances that may be considered include serious
        illness or injury, bereavement, venue failure, significant
        technical errors caused by Whistll, or other events genuinely
        beyond your reasonable control. We aim to respond within 10
        working days.
      </p>
      <h3>4.8 Payment processing</h3>
      <p>
        Payments are processed through third-party providers including
        Stripe. By making a payment you agree that:
      </p>
      <ul>
        <li>your payment may be processed under third-party terms and privacy policies;</li>
        <li>Whistll does not store full payment card details;</li>
        <li>Whistll is not liable for third-party payment processing errors, outages, or failed authorisations; and</li>
        <li>you authorise the relevant charges shown at checkout.</li>
      </ul>
      <h3>4.9 Chargebacks and payment disputes</h3>
      <p>
        If you believe a charge is incorrect, unauthorised, or that you
        are entitled to a refund, you agree to contact Whistll first at{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>{" "}
        before raising a chargeback or dispute with your bank or card
        issuer. Whistll aims to respond to payment queries within 3
        working days.
      </p>
      <p>
        Where you raise a chargeback without first contacting Whistll,
        or where a chargeback is raised in circumstances Whistll
        reasonably considers unjustified (including but not limited to
        disputes relating to games you attended, services that were
        delivered as described, or refund requests outside of the refund
        eligibility windows), Whistll reserves the right to:
      </p>
      <ul>
        <li>investigate and contest the chargeback using evidence including booking records, attendance confirmations, and communication logs;</li>
        <li>recover from you any chargeback fees, administrative costs, and losses incurred by Whistll as a result of an unjustified chargeback;</li>
        <li>suspend or terminate your account pending resolution; and</li>
        <li>where applicable, report fraudulent chargebacks to the relevant authorities.</li>
      </ul>
      <p>
        Nothing in this clause affects your statutory consumer rights,
        including rights to refunds where the service has not been
        delivered or is materially defective.
      </p>
      <h3>4.10 UK and US consumer rights</h3>
      <p>
        Nothing in these Terms affects mandatory statutory rights you may
        have under applicable UK consumer law (including the Consumer
        Rights Act 2015) or applicable US federal, state, or local
        consumer protection laws.
      </p>

      <h2>5. Community conduct, reliability &amp; flags</h2>
      <h3>5.1 Community rules</h3>
      <p>
        By using Whistll you agree to follow the community rules
        presented in the App, which cover respect for other players,
        fair play, reliability, media and highlights, and reporting. The
        community rules form part of these Terms.
      </p>
      <h3>5.2 Reliability score</h3>
      <p>
        Whistll tracks user reliability based on attendance,
        cancellations, and no-shows. Your reliability score is used to
        inform other players and to determine access to certain
        features.
      </p>
      <h3>5.3 No-shows</h3>
      <p>
        If you fail to attend a confirmed game without cancelling within
        the permitted window, you may be marked as a no-show. No-shows
        are determined through a combination of host reporting and peer
        confirmation via the post-match user interface. Repeat no-shows
        may result in temporary restrictions on booking, reduced
        reliability score, or account suspension.
      </p>
      <h3>5.4 Flags</h3>
      <p>Users may receive a flag on their account for:</p>
      <ul>
        <li>dishonest behaviour, including falsifying match results or attendance;</li>
        <li>repeated breaches of community rules;</li>
        <li>abuse of the reporting system; or</li>
        <li>other conduct that undermines the integrity of the platform.</li>
      </ul>
      <p>
        <strong>
          Two flags within a rolling 12-month period will result in an
          automatic 3-day ban from the platform.
        </strong>{" "}
        Further flags may result in longer bans or permanent account
        suspension at Whistll's sole discretion.
      </p>
      <h3>5.5 Reporting</h3>
      <p>
        Every user and piece of user content may be reported through
        in-app reporting tools. Reports are reviewed by Whistll
        administrators. Abusing the reporting system (including false or
        retaliatory reports) is itself a breach of these Terms.
      </p>

      <h2>6. Competitive games, teams, leagues &amp; disputes</h2>
      <h3>6.1 Compete feature</h3>
      <p>
        Users may create teams, issue and accept challenges, and compete
        against other teams. Captains manage their squads and must have
        the required number of confirmed and paid players before a
        challenge can be finalised.
      </p>
      <h3>6.2 Leagues</h3>
      <p>
        Users may participate in a maximum of three (3) leagues at any
        one time. League structures, win conditions, and durations are
        displayed in the App.
      </p>
      <h3>6.3 Result confirmation</h3>
      <p>
        Competitive game results are recorded through dual-captain
        confirmation. Where captains disagree, a peer vote among
        participating players determines the result. Where peer vote
        cannot resolve the dispute, Whistll administrators may manually
        override the result at their sole discretion.
      </p>
      <h3>6.4 Dishonest conduct</h3>
      <p>
        Falsifying match results, falsely disputing results, or
        otherwise attempting to manipulate the competitive system will
        result in a flag (see clause 5.4) and may result in additional
        sanctions including league disqualification.
      </p>
      <h3>6.5 Team clips and highlights</h3>
      <p>
        Teams may upload highlight footage to their team profiles. All
        such content is subject to the User Content provisions in
        section 7 and the content moderation provisions in section 8.
      </p>

      <h2>7. User content, reels &amp; sharing</h2>
      <h3>7.1 Scope</h3>
      <p>
        This section applies to all content you upload, post, publish,
        submit, or otherwise make available through the App ("User
        Content"), including profile images, usernames, comments,
        messages, highlights, game footage, team clips, training content,
        and any other materials.
      </p>
      <h3>7.2 Permitted content</h3>
      <p>
        You may upload and share content that is relevant to basketball,
        appropriate for a general audience, lawful, and uploaded with all
        necessary rights, permissions, and consents.
      </p>
      <h3>7.3 Prohibited content</h3>
      <p>
        You must not upload, post, transmit, or otherwise make available
        any content that:
      </p>
      <ul>
        <li>contains nudity, sexual content, or sexually explicit material;</li>
        <li>depicts, promotes, glorifies, or incites violence, dangerous conduct, or physical harm;</li>
        <li>includes harassment, bullying, intimidation, hate speech, or abusive behaviour;</li>
        <li>is defamatory, obscene, threatening, discriminatory, or hateful;</li>
        <li>is unrelated to basketball or the intended purpose of the App;</li>
        <li>infringes any copyright, trade mark, privacy, publicity, data protection, or other rights;</li>
        <li>contains unlawful, misleading, deceptive, or fraudulent material;</li>
        <li>promotes illegal goods, illegal activities, self-harm, or other harmful conduct; or</li>
        <li>is otherwise inappropriate or inconsistent with the intended use of Whistll.</li>
      </ul>
      <h3>7.4 Game clips and reels</h3>
      <p>
        You may upload clips from games you have participated in within
        the App for up to forty-eight (48) hours after the game end
        time. By being physically present on a basketball court during a
        Whistll-organised game, you acknowledge that other participants
        may capture action footage and post it to the App. This is
        considered implied consent for action-based game footage.
      </p>
      <h3>7.5 Right to request removal</h3>
      <p>
        Notwithstanding clause 7.4, you may request the removal of any
        content in which you are identifiable by contacting us at{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        . We will review all such requests and remove content where
        appropriate, taking into account the legitimate interests of the
        uploader and the nature of the content. Requests relating to
        abusive, harmful, or private content will be prioritised.
      </p>
      <h3>7.6 User warranties</h3>
      <p>By posting User Content, you warrant that:</p>
      <ul>
        <li>you own the content or have all necessary rights, licences, permissions, and consents;</li>
        <li>your content does not violate any law or the rights of any third party;</li>
        <li>any individuals identifiable in the content have provided any required consent beyond the implied consent in clause 7.4; and</li>
        <li>the content is accurate to the extent it purports to state facts.</li>
      </ul>
      <h3>7.7 Licence to Whistll</h3>
      <p>
        By uploading or posting User Content, you grant Whistll a
        non-exclusive, royalty-free, worldwide, transferable,
        sublicensable licence to host, store, reproduce, display,
        distribute, adapt, and use that content for the operation,
        improvement, moderation, and promotion of the App and our
        services. This licence continues for as long as the content
        remains on the App and for a reasonable period thereafter for
        backup, archival, compliance, or enforcement purposes.
      </p>
      <h3>7.8 No commercial use by other users</h3>
      <p>
        Users may not reproduce, redistribute, or commercially exploit
        User Content posted by other users outside the App without the
        uploader's express written consent.
      </p>
      <h3>7.9 External sharing</h3>
      <p>
        Where the App enables sharing of User Content to third-party
        platforms (such as Instagram, TikTok, or other social networks),
        your use of those platforms is subject to their own terms.
        Whistll is not responsible for content once it leaves the App.
      </p>
      <h3>7.10 Ownership</h3>
      <p>
        You retain ownership of your User Content, subject to the
        licence granted above.
      </p>

      <h2>8. Content moderation</h2>
      <h3>8.1 Automated review</h3>
      <p>
        All image and video uploads are automatically reviewed using
        content moderation tools, including AWS Rekognition. Content
        flagged as potentially inappropriate may be rejected
        automatically, held for manual review, or published depending on
        the confidence score.
      </p>
      <h3>8.2 User reports</h3>
      <p>
        Every piece of User Content includes a reporting mechanism.
        Reported content is reviewed by Whistll administrators.
      </p>
      <h3>8.3 Moderation rights</h3>
      <p>
        Whistll reserves the right, at its sole discretion and without
        prior notice, to review, monitor, remove, restrict, or disable
        access to any User Content; suspend or terminate user accounts;
        investigate breaches of these Terms; and report content or
        conduct to law enforcement, regulators, or other relevant
        parties where appropriate.
      </p>
      <h3>8.4 No obligation to monitor</h3>
      <p>
        Whistll is under no obligation to monitor User Content
        proactively but may do so at its discretion.
      </p>
      <h3>8.5 No endorsement</h3>
      <p>
        Whistll does not endorse, verify, control, or assume
        responsibility for User Content posted by users. You access and
        rely on User Content at your own risk.
      </p>

      <h2>9. Direct messaging, follows &amp; blocking</h2>
      <h3>9.1 Direct messaging</h3>
      <p>
        Direct messaging between users is opt-in and disabled by
        default. Users may enable messaging in their account settings.
        Where a user has not enabled messaging, the App will clearly
        indicate this on their profile.
      </p>
      <h3>9.2 Message privacy</h3>
      <p>
        Direct messages are private between the two users involved.
        Whistll may access messages for moderation, safety, legal
        compliance, or as described in our Privacy Policy.
      </p>
      <h3>9.3 Follows and feed</h3>
      <p>
        Users may follow other users. Public content from followed users
        may appear in your feed.
      </p>
      <h3>9.4 Blocking</h3>
      <p>
        You may block any user from your profile or from within a
        conversation. Blocking prevents further messages, removes the
        blocked user from your feeds, and ensures that games created or
        joined by blocked users do not appear to you in Open Runs,
        Compete, or Leagues discovery (and vice versa).
      </p>
      <h3>9.5 Free agent visibility</h3>
      <p>
        Certain features, including free agent visibility on your home
        court, are opt-in. You may enable or disable these features at
        any time in your account settings.
      </p>

      <h2>10. Intellectual property</h2>
      <p>
        All intellectual property rights in and to the App, including
        its software, design, text, graphics, logos, trade marks,
        branding, databases, functionality, and underlying technology,
        are owned by or licensed to Whistll. Except as expressly
        permitted by these Terms, you may not copy, reproduce,
        distribute, modify, reverse engineer, create derivative works
        from, republish, transmit, sell, or exploit any part of the App
        without our prior written consent.
      </p>

      <h2>11. Privacy &amp; data</h2>
      <p>
        Your privacy is important to us. Our{" "}
        <a href="/privacy-policy" className="underline underline-offset-2">
          Privacy Policy
        </a>{" "}
        explains how we collect, use, store, share, and protect your
        personal data and is incorporated into these Terms by reference.
        By using the App, you acknowledge that we may process personal
        data in accordance with our Privacy Policy.
      </p>

      <h2>12. Liability &amp; disclaimers</h2>
      <h3>12.1 App availability</h3>
      <p>
        We aim to make the App available at all times but do not
        guarantee uninterrupted, secure, timely, or error-free access.
        We may suspend, withdraw, or restrict all or part of the App for
        maintenance, updates, legal reasons, or reasons beyond our
        reasonable control.
      </p>
      <h3>12.2 Platform-only role</h3>
      <p>
        Whistll acts solely as an intermediary platform. Whistll does
        not itself provide venue services, sports supervision, coaching,
        staffing, transport, physical facilities, or event operation.
      </p>
      <h3>12.3 No liability for user interactions</h3>
      <p>
        Whistll is not responsible for the acts, omissions, behaviour,
        statements, conduct, or reliability of any user, whether online
        or offline. Any interaction with another user is at your own
        risk.
      </p>
      <h3>12.4 Physical activity disclaimer and acknowledgement</h3>
      <p>
        Basketball and related activities involve inherent physical
        risks, including but not limited to sprains, strains, fractures,
        collisions with other players, and injuries arising from the
        condition of the playing surface or facility.
      </p>
      <p>
        By joining any game, session, training, or activity arranged
        through the App, you expressly acknowledge and accept that:
      </p>
      <ul>
        <li>participation is entirely at your own risk;</li>
        <li>you are medically, physically, and otherwise fit to participate;</li>
        <li>you have no known medical condition that would make participation unsafe for you or other players;</li>
        <li>you will comply with the instructions of the host and any reasonable venue rules;</li>
        <li>you will not participate under the influence of alcohol or substances that could impair your ability to play safely;</li>
        <li>you are responsible for your own safety equipment and clothing; and</li>
        <li>Whistll is not liable for any injury, illness, loss, theft, damage, accident, or death arising from participation in any activity, except to the extent liability cannot lawfully be excluded.</li>
      </ul>
      <p>
        During account creation, users will be required to acknowledge
        this physical activity risk in the App. Continued use of the
        App constitutes ongoing acknowledgement of these risks.
      </p>
      <h3>12.5 Third-party services and venues</h3>
      <p>
        Where the App links to, integrates with, or facilitates access
        to third-party services, content, venues, or payment providers,
        Whistll is not responsible for their availability, quality,
        conduct, legality, security, or performance.
      </p>
      <h3>12.6 Limitation of liability</h3>
      <p>
        To the fullest extent permitted by law, Whistll's total
        aggregate liability arising out of or in connection with these
        Terms or your use of the App shall not exceed the total amount
        paid by you directly to Whistll in the 12 months preceding the
        event giving rise to the claim, or £100 / $100 if no such
        payments were made.
      </p>
      <p>
        To the fullest extent permitted by law, Whistll shall not be
        liable for any indirect, incidental, consequential, special,
        exemplary, or punitive losses or damages, including loss of
        profits, revenue, data, goodwill, business opportunity,
        anticipated savings, or reputation.
      </p>
      <h3>12.7 No exclusion where not lawful</h3>
      <p>
        Nothing in these Terms excludes or limits liability for death
        or personal injury caused by our negligence; fraud or fraudulent
        misrepresentation; or any liability that cannot lawfully be
        excluded or limited under applicable law.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Whistll, its
        affiliates, directors, officers, employees, contractors, agents,
        licensors, and partners from any and all claims, demands,
        actions, losses, liabilities, damages, judgments, costs, and
        expenses, including reasonable legal fees, arising out of or in
        connection with your use of the App; your User Content; your
        breach of these Terms; your violation of any applicable law; or
        your violation of the rights of any third party.
      </p>

      <h2>14. Changes to these terms</h2>
      <p>
        We may amend these Terms from time to time. Where we make
        material changes, we may notify you through the App, by email,
        or by other reasonable means before the changes take effect.
        Your continued use of Whistll after the effective date
        constitutes acceptance. If you do not agree, you must stop using
        the App.
      </p>

      <h2>15. Governing law &amp; disputes</h2>
      <p>
        If you are a user in the UK, these Terms are governed by the
        laws of England and Wales, and the courts of England and Wales
        shall have jurisdiction, subject to any mandatory consumer
        rights.
      </p>
      <p>
        If you are a user in the United States, these Terms shall be
        governed by the laws of the State of Delaware, without regard to
        conflict of law principles, and disputes shall be brought in the
        state or federal courts located in Delaware, unless otherwise
        required by law.
      </p>
      <p>
        If you are a user in Canada, these Terms shall be governed by
        the laws of the Province of Ontario, Canada.
      </p>
      <p>
        If you are a user in Australia, these Terms shall be governed by
        the laws of New South Wales, Australia.
      </p>
      <p>
        If you are a user in New Zealand, these Terms shall be governed
        by the laws of New Zealand, and disputes shall be brought in the
        courts of New Zealand.
      </p>
      <p>
        Before starting formal legal proceedings, you agree to contact
        us first at{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>{" "}
        and attempt to resolve the dispute informally.
      </p>

      <h2>16. Severability</h2>
      <p>
        If any provision of these Terms is found to be unlawful,
        invalid, or unenforceable, that provision shall be severed and
        the remaining provisions shall remain in full force and effect.
      </p>

      <h2>17. Entire agreement</h2>
      <p>
        These Terms, together with our{" "}
        <a href="/privacy-policy" className="underline underline-offset-2">
          Privacy Policy
        </a>
        , the{" "}
        <a href="/community-rules" className="underline underline-offset-2">
          Community Rules
        </a>
        , and any additional terms expressly incorporated by reference,
        constitute the entire agreement between you and Whistll
        regarding your use of the App.
      </p>

      <h2>18. Contact us</h2>
      <p>
        If you have any questions, concerns, or complaints, please
        contact us at:
      </p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        <br />
        <strong>Address:</strong> 71-75 Shelton Street, Covent Garden,
        London, WC2H 9JQ
        <br />
        <strong>Company number:</strong> 17172530
      </p>
      <p>We aim to respond within 3 working days.</p>
    </LegalPlaceholder>
  );
}
